import axios from 'axios';
import { getApiBase, getAuthHeaders } from './api';

export const UNAUTHORIZED_EVENT = 'veyage:unauthorized';

// Apply protection to both existing fetch callers and Axios callers. Never send
// session/CSRF material to third-party APIs.
export function installBrowserSecurity() {
  const rawFetch = window.fetch.bind(window);
  const api = new URL(getApiBase(), window.location.origin);
  const isApi = value => {
    const url = new URL(value, window.location.origin);
    return url.origin === api.origin && (url.pathname === api.pathname || url.pathname.startsWith(api.pathname + '/'));
  };
  const unsafe = method => !['GET', 'HEAD', 'OPTIONS'].includes((method || 'GET').toUpperCase());
  let pendingCsrf = null;
  const fetchCsrf = async () => {
    const response = await rawFetch(`${getApiBase()}/auth/csrf`, { credentials: 'include', cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to establish secure browser request');
    return (await response.json()).csrf_token;
  };
  const csrf = () => {
    if (!pendingCsrf) pendingCsrf = fetchCsrf().finally(() => { pendingCsrf = null; });
    return pendingCsrf;
  };
  axios.interceptors.request.use(async config => {
    // Resolve legacy relative API URLs before deciding whether to attach CSRF.
    if (!config.baseURL && config.url?.startsWith('/') && !config.url.startsWith('//')) {
      config.url = getApiBase() + (config.url === '/api' ? '' : config.url.startsWith('/api/') ? config.url.slice(4) : config.url);
    }
    const target = new URL(config.url, new URL(config.baseURL || window.location.origin, window.location.origin)).href;
    if (!isApi(target)) return config;
    config.withCredentials = true;
    config.headers = axios.AxiosHeaders.from(config.headers);
    config.headers.delete("Authorization");
    for (const [name, value] of Object.entries(getAuthHeaders())) {
      if (name.toLowerCase() !== 'content-type' && name.toLowerCase() !== 'authorization') config.headers.set(name, value);
    }
    if (unsafe(config.method)) config.headers.set('X-CSRF-Token', await csrf());
    return config;
  });
  // Tell AuthContext about API 401s so an expired session drops to the login
  // screen instead of leaving the app stuck on error states until a refresh.
  const reportUnauthorized = url => window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail: { url } }));
  axios.interceptors.response.use(response => response, error => {
    const config = error.config;
    if (error.response?.status === 401 && config?.url) {
      const target = new URL(config.url, new URL(config.baseURL || window.location.origin, window.location.origin)).href;
      if (isApi(target)) reportUnauthorized(target);
    }
    return Promise.reject(error);
  });
  window.fetch = async (input, init = {}) => {
    const url = input instanceof Request ? input.url : String(input);
    if (!isApi(url)) return rawFetch(input, init);
    const method = init.method || (input instanceof Request ? input.method : 'GET');
    const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
    headers.delete('Authorization');
    if (unsafe(method)) headers.set('X-CSRF-Token', await csrf());
    const response = await rawFetch(input, { ...init, headers, credentials: 'include' });
    if (response.status === 401) reportUnauthorized(url);
    return response;
  };
  for (const name of ['localStorage', 'sessionStorage']) {
    try { window[name].removeItem('token'); } catch {}
  }
}
